// src/components/CarsPage.tsx
import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Card } from "primereact/card";
import { Carousel } from "primereact/carousel";
import { Helmet } from "react-helmet-async";
import "../CSS/CarsPage.css";
import axios from "axios";

interface Car {
  _id: string;
  id?: string;
  title: string;
  text: string;
  imageUrl?: string;
  galleryImages: string[];
  youtubeUrl?: string;
  price: number;
  model: string;
  year: number;
  mileage: number;
  createdAt: number;
  type: "car";
}

const CarsPage: React.FC = () => {
  const [car, setCar] = useState<Car | null>(null);
  const [loading, setLoading] = useState(true);
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const url = `${window.location.origin}${location.pathname}`;

  const getYouTubeVideoId = (url: string) => {
    if (!url) return null;

    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s]+)/,
      /youtube\.com\/watch\?.*v=([^&\s]+)/,
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }

    return null;
  };

  useEffect(() => {
    const fetchCar = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API}/cars/${id}`
        );
        if (response.data) {
          setCar(response.data);
        } else {
          console.error("Car not found for the given ID");
        }
      } catch (error) {
        console.error("Error fetching car:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCar();
  }, [id]);

  if (loading) {
    return (
      <div className="car-page-container">
        <div style={{ textAlign: "center", padding: "2rem" }}>
          Loading car details...
        </div>
      </div>
    );
  }

  if (!car) {
    return (
      <div className="car-page-container">
        <div style={{ textAlign: "center", padding: "2rem" }}>
          Car not found
        </div>
      </div>
    );
  }

  const carImages = [car.imageUrl, ...car.galleryImages].filter(Boolean);
  const carouselItems = [...carImages];

  // Add YouTube video as the last slide if it exists
  if (car.youtubeUrl && getYouTubeVideoId(car.youtubeUrl)) {
    carouselItems.push("youtube");
  }

  const carouselItemTemplate = (item: string) => {
    if (item === "youtube") {
      return (
        <div className="car-carousel-item video-slide">
          <div className="video-container">
            <iframe
              src={`https://www.youtube.com/embed/${getYouTubeVideoId(
                car.youtubeUrl!
              )}`}
              title="Car Video"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="car-video-iframe"
            />
          </div>
        </div>
      );
    }
    return (
      <div className="car-carousel-item">
        <img src={item} alt={car.title} className="car-carousel-image" />
      </div>
    );
  };

  const shareUrl = `${process.env.REACT_APP_API}/cars/ssr/${car._id}`;

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(
        `${car.model} ${car.year} - ${car.price.toLocaleString()} Read more at: ${shareUrl}`
      );
    } catch (err) {
      console.error("Failed to copy URL:", err);
    }
  };

  return (
    <div className="car-page-container">
      <Helmet>
        <title>{car?.title} - Taswik</title>
        <meta name="description" content={car?.text.substring(0, 160)} />
        <meta property="og:title" content={car?.title} />
        <meta property="og:description" content={car?.text.substring(0, 160)} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="website" />
        {car?.imageUrl && (
          <>
            <meta property="og:image" content={car.imageUrl} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
          </>
        )}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={car?.title} />
        <meta
          name="twitter:description"
          content={car?.text.substring(0, 160)}
        />
        {car?.imageUrl && <meta name="twitter:image" content={car.imageUrl} />}
      </Helmet>
      <Card className="car-details-card">
        <div className="car-header">
          <h1>{car.title}</h1>
        </div>

        {carouselItems.length > 0 && (
          <div className="car-carousel-container">
            <Carousel
              value={carouselItems}
              numVisible={1}
              numScroll={1}
              itemTemplate={carouselItemTemplate}
              className="car-carousel"
            />
          </div>
        )}

        <div className="social-share-buttons">
          <a
            className="social-btn whatsapp"
            rel="noreferrer noopener nofollow"
            href={`https://wa.me/?text=${encodeURIComponent(
              `${car.model} ${car.year} - ${car.price.toLocaleString()} Read more at: ${shareUrl}`
            )}`}
            target="_blank"
            title="Share on WhatsApp"
            aria-label="Share on WhatsApp"
          >
            <i className="pi pi-whatsapp"></i>
          </a>
          <a
            className="social-btn instagram"
            rel="noreferrer noopener nofollow"
            href={`https://www.instagram.com/sharer/sharer.php?u=${encodeURIComponent(
              `${car.model} ${car.year} - ${car.price.toLocaleString()} Read more at: ${shareUrl}`
            )}`}
            target="_blank"
            title="Share on Instagram"
            aria-label="Share on Instagram"
          >
            <i className="pi pi-instagram"></i>
          </a>
          <a
            className="social-btn facebook"
            rel="noreferrer noopener nofollow"
            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
              shareUrl
            )}`}
            target="_blank"
            title="Share on Facebook"
            aria-label="Share on Facebook"
          >
            <i className="pi pi-facebook"></i>
          </a>
          <button
            type="button"
            className="social-btn share-native"
            aria-label="Share"
            onClick={() => {
              const shareData = {
                url: shareUrl,
              };
              navigator.share(shareData).catch(console.error);
            }}
          >
            <i className="pi pi-share-alt"></i>
          </button>
          <button
            className="social-btn copy-link"
            onClick={copyUrl}
            title="Copy Link"
            aria-label="Copy Link"
          >
            <i className="pi pi-link"></i>
          </button>
        </div>

        <div className="car-info">
          <div className="car-specs">
            <div className="spec-item">
              <span className="spec-label">Model:</span>
              <span className="spec-value">{car.model}</span>
            </div>
            <div className="spec-item">
              <span className="spec-label">Year:</span>
              <span className="spec-value">{car.year}</span>
            </div>
            <div className="spec-item">
              <span className="spec-label">Mileage:</span>
              <span className="spec-value">
                {car.mileage.toLocaleString()} km
              </span>
            </div>
            <div className="spec-item">
              <span className="spec-label">Price:</span>
              <span className="spec-value">${car.price.toLocaleString()}</span>
            </div>
          </div>

          <div className="car-description">
            <h2>Description</h2>
            <p>{car.text}</p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default CarsPage;
